//@ts-nocheck
import React from "react";
import { Select } from "antd";
import { useNavigate, useLocation } from "react-router-dom";

interface NavigationDropdownProps {
  withQuoteId?: (path: string) => string; // Optional function to add quoteId to paths
  className?: string;
  hideValues?: string[]; // Optional list of item values to hide from the dropdown
}

// Menu configuration - single source of truth
export const MENU_ITEMS = [
  { value: "dashboard", label: "Dashboard", path: "/dashboard" },
  { value: "ateDashboard", label: "ATE Dashboard", path: "/ate-dashboard" },
  // Customer Email and Dealer Email are one page now, switched from its own
  // left sidebar — this is the single entry point for both.
  { value: "customerDealerEmail", label: "Emails", path: "/customer-email" },
  { value: "quotes", label: "Quotes", path: "/Quotes" },
  { value: "openOrderReport", label: "Open Order Report", path: "/open-order" },
  {
    value: "orderTrackingCenter",
    label: "Order Tracking Center",
    path: "/order-tracking",
  },
  { value: "otcSnapshot", label: "OTC Snapshot", path: "/otc" },
  { value: "quotesWtb", label: "Quotes WTB", path: "/quotes-wtb" },
  {
    value: "quotesResponses",
    label: "Quotes Responses",
    path: "/quotes-responses",
  },
  {
    value: "quotesResponsesSimple",
    label: "Quotes Responses Simple",
    path: "/quotes-responses-simple",
  },
  {
    value: "pendingQuoteResponses",
    label: "Pending Quote Responses",
    path: "/pending-quote-responses",
  },
  { value: "inventory", label: "Inventory", path: "/inventory" },
  { value: "category", label: "Category", path: "/category" },
  { value: "source", label: "Source", path: "/source" },
  { value: "representative", label: "Representative", path: "/representative" },
  { value: "company", label: "Company", path: "/company" },
  { value: "offices", label: "Offices", path: "/offices" },
  { value: "settings", label: "Settings", path: "/settings" },
  { value: "emailMasters", label: "Email Masters", path: "/email" },
  { value: "profile", label: "Profile", path: "/profile" },
];

// Comma-separated list of user emails allowed to see the "Emails" tab
// (Customer Email + Dealer Email). Any other logged-in user will not see it
// in the list.
const EMAIL_LINK_MENU_VALUES = ["customerDealerEmail"];

export const canSeeEmailLinkTabs = () => {
  try {
    const email = (JSON.parse(localStorage.getItem("user") || "{}")?.email || "")
      .trim()
      .toLowerCase();
    const allowedEmails = (process.env.REACT_APP_ORDER_TRACKING_EMAILS || "")
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
    return allowedEmails.includes(email);
  } catch {
    return false;
  }
};

// Create mapping from path to value for easy lookup
const PATH_TO_VALUE = MENU_ITEMS.reduce(
  (acc, item) => {
    acc[item.path] = item.value;
    return acc;
  },
  {} as Record<string, string>,
);

const NavigationDropdown: React.FC<NavigationDropdownProps> = ({
  withQuoteId,
  className = "!w-32",
  hideValues = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const showEmailLinkTabs = canSeeEmailLinkTabs();

  const visibleItems = MENU_ITEMS.filter(
    (item) =>
      !hideValues.includes(item.value) &&
      (showEmailLinkTabs || !EMAIL_LINK_MENU_VALUES.includes(item.value)),
  );

  // Get current value based on path
  const getCurrentValue = () => {
    const path = location.pathname;
    return PATH_TO_VALUE[path] || "quotes";
  };

  const handleChange = (value: string) => {
    const selectedItem = MENU_ITEMS.find((item) => item.value === value);
    if (selectedItem) {
      let path = selectedItem.path;
      // Add quoteId to path if the function is provided
      if (withQuoteId) {
        path = withQuoteId(path);
      }
      // Hand the destination page where we're jumping from, so a page that
      // wants a precise "Back" (e.g. the Emails page) can return here
      // directly instead of unwinding every dropdown hop as its own
      // history entry.
      navigate(path, {
        state: { from: `${location.pathname}${location.search}` },
      });
    }
  };

  return (
    <Select
      value={getCurrentValue()}
      popupClassName="sources-select-dropdown"
      className={className}
      size="small"
      onChange={handleChange}
      options={visibleItems.map((item) => ({
        value: item.value,
        label: item.label,
      }))}
    />
  );
};

export default NavigationDropdown;
